import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Marquee from "./components/Marquee";
import Concept from "./components/Concept";
import ProductSection from "./components/ProductSection";
import Features from "./components/Features";
import Products from "./components/Products";
import News from "./components/News";
import Contact from "./components/Contact";
import Footer from "./components/Footer";

const homeStructuredData = [
  {
    "@type": "Organization",
    name: "ハヤブサワークス",
    alternateName: "Hayabusa Works",
    url: "https://raigeki-hayabusa.com",
    logo: "https://static.readdy.ai/image/6598d90c230f37b72c6551167ff1d4d5/cb43db14c54470be4f999f19f3077426.jpeg",
    description: "3Dプリント技術で作る釣り用カゴ「雷撃カゴ」を製造する釣具メーカー。圧倒的な飛距離、飛行安定性、餌の放出性を実現。",
    email: "info@raigeki-hayabusa.com",
    address: {
      "@type": "PostalAddress",
      addressLocality: "松江市",
      addressRegion: "島根県",
      addressCountry: "JP",
    },
    sameAs: [
      "https://www.instagram.com/hayabusaworks2024",
      "https://www.youtube.com/@HayabusaWorks2024",
      "https://x.com/raigekiakamaru",
    ],
  },
  {
    "@type": "WebSite",
    name: "ハヤブサワークス",
    url: "https://raigeki-hayabusa.com",
    description: "ハヤブサワークスは3Dプリント技術で作る釣り用カゴ「雷撃カゴ」を製造する釣具メーカー。圧倒的な飛距離、飛行安定性、餌の放出性を実現。",
    inLanguage: "ja",
  },
  {
    "@type": "Product",
    name: "雷撃カゴ 00",
    description: "3Dプリント製の超近距離用カゴウキ。SSサイズ、1.5号オモリ内蔵。対象魚種：グレ・チヌ・アジ・イサキ。",
    image: "https://static.readdy.ai/image/6598d90c230f37b72c6551167ff1d4d5/852857a90ff1089cc2186ec01c86735b.jpeg",
    offers: {
      "@type": "Offer",
      price: "2500",
      priceCurrency: "JPY",
      availability: "https://schema.org/InStock",
    },
  },
  {
    "@type": "Product",
    name: "雷撃カゴ 01",
    description: "3Dプリント製の近距離用カゴウキ。Sサイズ、3号オモリ内蔵。対象魚種：グレ・イサキ・マダイ。",
    image: "https://static.readdy.ai/image/6598d90c230f37b72c6551167ff1d4d5/7e3a0227b9a67f2d8bf46fa3db67a56c.jpeg",
    offers: {
      "@type": "Offer",
      price: "2900",
      priceCurrency: "JPY",
      availability: "https://schema.org/InStock",
    },
  },
  {
    "@type": "Product",
    name: "雷撃カゴ 02",
    description: "3Dプリント製の中距離用カゴウキ。Mサイズ、6号オモリ内蔵。対象魚種：グレ・イサキ・マダイ・青物。",
    image: "https://static.readdy.ai/image/6598d90c230f37b72c6551167ff1d4d5/9350d304fa2839e0df7c8ffe3b078c78.jpeg",
    offers: {
      "@type": "Offer",
      price: "3200",
      priceCurrency: "JPY",
      availability: "https://schema.org/InStock",
    },
  },
  {
    "@type": "Product",
    name: "雷撃カゴ 03",
    description: "3Dプリント製の遠距離用カゴウキ。Lサイズ、8号オモリ内蔵。対象魚種：大型青物・マダイ。",
    image: "https://static.readdy.ai/image/6598d90c230f37b72c6551167ff1d4d5/0e569861cced3e3f9845666a20ef5393.jpeg",
    offers: {
      "@type": "Offer",
      price: "3500",
      priceCurrency: "JPY",
      availability: "https://schema.org/InStock",
    },
  },
  {
    "@type": "Product",
    name: "ショート天秤",
    description: "雷撃カゴ専用ショート天秤。長さ約20cm。針掛けリブ（突起）付き。糸落ちが速く、潮が速い時や魚のタナが深い時に最適。推奨カゴサイズ：00・01。",
    image: "https://static.readdy.ai/image/6598d90c230f37b72c6551167ff1d4d5/5df6b785e25d020e60fe1aa027fa88ec.jpeg",
    offers: {
      "@type": "Offer",
      price: "400",
      priceCurrency: "JPY",
      availability: "https://schema.org/InStock",
    },
  },
  {
    "@type": "Product",
    name: "ロング天秤",
    description: "雷撃カゴ専用ロング天秤。長さ約35cm。針掛けリブ（突起）付き。糸絡みが発生しにくく、安定した仕掛けを実現。推奨カゴサイズ：02・03。",
    image: "https://static.readdy.ai/image/6598d90c230f37b72c6551167ff1d4d5/8a3c651874a8528a0858a46e78f1c7ad.jpeg",
    offers: {
      "@type": "Offer",
      price: "850",
      priceCurrency: "JPY",
      availability: "https://schema.org/InStock",
    },
  },
];

export default function Home() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 150);
      }
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-100">
      <SEOHead
        title="ハヤブサワークス｜3Dプリント製 雷撃カゴ｜飛ぶ、を極める"
        description="ハヤブサワークスは3Dプリント技術で作る釣り用カゴ「雷撃カゴ」を製造する釣具メーカー。圧倒的な飛距離、飛行安定性、餌の放出性を実現。本気の釣り人に選ばれる次世代フィッシングギア。"
        keywords="雷撃カゴ,ハヤブサワークス,3Dプリント 釣り具,遠投カゴ,フカセ釣り カゴ"
        canonicalPath="/"
        ogImage="https://static.readdy.ai/image/6598d90c230f37b72c6551167ff1d4d5/cfb5afee631d88a7b12672494ae842fb.jpeg"
        structuredData={homeStructuredData}
      />
      <Navbar />
      <main>
        <Hero />
        <Marquee />
        <Concept />
        <Features />
        <ProductSection />
        <Products />
        <News />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}